import { PLACEHOLDER_RICH_TEXT_DIV } from "canvas/Constants";
import * as _ from "lodash";
import { ActionCreator, AnyAction, bindActionCreators, Dispatch } from "redux";
import { BatchAction, batchActions } from "redux-batched-actions";
import { ThunkAction } from "redux-thunk";
import {
	CanvasMode,
	GeometryTypes,
	IPageObjectTypes,
	PlaceholderTextFormat,
} from "../../widgets/interfaces/model/enumerations/index";
import { ICanvasDimension, ICanvasEditorState, IPage } from "../../widgets/interfaces/model/index";
import { IGroupShape } from "../../widgets/interfaces/model/PageObjects";
import { IShape } from "../../widgets/interfaces/model/PageObjects/Shape";
import { PageObjectType } from "../../widgets/interfaces/model/Types";
import { getSelectedBaseObjects } from "../../widgets/selectors/index";
import { getCurveSelectionPoint } from "../../widgets/util";
import { CanvasEditorConstants } from "../reducers/CanvasEditor";
import {
	ISetCanvasContainerAction,
	ISetCanvasDimensionAction,
	ISetCanvasZoomAction,
	ISetEditableAction,
	ISetIsCanvasTemplateAction, ISetPlaceholderTextFormatAction, ISetSnapOptionsAction
} from "../reducers/CanvasEditorOperations";
import {
	GuideActionConstants,
	IApplyTextFormattingAction,
	IDeselectAllObjectAction,
	IResetGuidesAction,
	ISetCanvasModeAction,
	ISetCanvasPanAction,
	ISetFirstCharStyleAction,
	ISetFormatPainterAction,
	ISetFroalaLicenseAction,
	ISetSelectedCurvePointAction
} from "../reducers/index";
import { SelectionConstants } from "../reducers/Selection";
import { convertShapeToCustom, firstCharacterStyles } from "../reducers/util";

/**
 * Set canvas dimension action creator.
 * @param dimension { ICanvasDimension } dimension.
 * @returns Set canvas dimension  action.
 */
export const setCanvasDimension: ActionCreator<ISetCanvasDimensionAction> =
	(dimension: ICanvasDimension): ISetCanvasDimensionAction => ({
		editorHeight: dimension.editorHeight,
		editorWidth: dimension.editorWidth,
		transient: true,
		type: CanvasEditorConstants.SET_CANVAS_DIMENSION,
	});

/**
 * Set canvas dimension action creator.
 * @param dimension { ICanvasDimension } dimension.
 * @returns Set canvas dimension  action.
 */
export const setPanningMode: ActionCreator<ISetCanvasPanAction> =
	(pan: boolean): ISetCanvasPanAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_CANVAS_PAN,
		pan,
	});

/**
 * Set snap to grid option on and off.
 * @param snapOption { boolean } snap options .
 * @returns set snap to grid  action.
 */
export const setSnapToGrid: ActionCreator<ISetSnapOptionsAction> =
	(snapOption: boolean): ISetSnapOptionsAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_SNAP_TO_GRID,
		snapOption,
	});

/**
 * Set snap to guide option on and off.
 * @param snapOption { boolean } snap options .
 * @returns set snap to guide  action.
 */
export const setSnapToGuide: ActionCreator<ISetSnapOptionsAction> =
	(snapOption: boolean): ISetSnapOptionsAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_SNAP_TO_GUIDE,
		snapOption,
	});

/**
 * Set canvas mode.
 * @param mode {CanvasMode} Canvas mode to set.
 * @param preserveSelection {boolean} This flag will be used to decide whether to deselect all objects or not.
 */
export const setCanvasMode: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(mode: CanvasMode, preserveSelection?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const actionList: AnyAction[] = [];
			// Deselect all selected object action
			const deselectAllObjectsAction: IDeselectAllObjectAction = {
				transient: true,
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};

			// Set canvas mode action
			actionList.push({
				transient: true,
				type: CanvasEditorConstants.SET_CANVAS_MODE,
				mode,
			} as ISetCanvasModeAction);

			if (mode === CanvasMode.DRAW) {
				actionList.push(deselectAllObjectsAction);
				actionList.push({
					textStyles: undefined,
					type: SelectionConstants.SET_FIRST_CHAR_STYLE,
				} as ISetFirstCharStyleAction);
			} else if (!preserveSelection) {
				// Deselect all objects if preserveSelection flag is false or undefined
				actionList.push(deselectAllObjectsAction);
			}
			return dispatch(batchActions(actionList));
		};

/**
 * Set format painter action creator.
 * @param isFormatPainter { Boolean } format painter flag.
 * @returns Set format painter action.
 */
export const setFormatPainter: ActionCreator<ThunkAction<BatchAction | undefined, ICanvasEditorState, void, BatchAction | undefined>>
	= (isFormatPainter: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
			const actions = [];
			if (isFormatPainter) {
				// If format painter is true, then setting first char styles in state
				// That could be use while apply applying format painter
				const state: ICanvasEditorState = getState();
				const pageObject = getSelectedBaseObjects(state);

				if (pageObject && pageObject.length) {
					const shape = pageObject[0] as IShape;
					const textContent = shape.txBody && shape.txBody.content;
					if (textContent) {
						const textStyles = firstCharacterStyles(textContent);
						const valueStyles = firstCharacterStyles(textContent, PlaceholderTextFormat.VALUE);
						actions.push({
							transient: true,
							type: SelectionConstants.SET_FORMAT_PAINTER_TEXT_STYLE,
							textStyles,
							valueStyles,
						} as IApplyTextFormattingAction);
					}
				}
			}
			// Set format painter action
			const setFormatPainterAction: ISetFormatPainterAction = {
				isFormatPainter,
				transient: true,
				type: CanvasEditorConstants.SET_FORMAT_PAINTER,
			};
			actions.push(setFormatPainterAction);
			return dispatch(batchActions(actions));
		};

/**
 * Edit selected page object. Editing is allowed only if one page object is selected.
 * Canvas mode will be set in following manner
 * Shape Type : Custom Geometry, Mode: EDIT_CURVE
 * Shape Type : Preset Geometry, Mode: EDIT_TEXT | EDIT_PICTURE
 * @returns {ISetCanvasModeAction}
 */
export const editPageObject: ActionCreator<ThunkAction<BatchAction | undefined, ICanvasEditorState, void, BatchAction | undefined>> =
	(custom?: boolean, exitEdit?: boolean, shapeId?: string) =>
		(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction | undefined => {
			const state: ICanvasEditorState = getState();
			const { groupSelectionIndices, selectedPageObjects, selectedPage } = state.selection.present;
			const currentCanvasMode = state.canvasEditor.mode;
			const actions: AnyAction[] = [];
			// Throw error message if there is no page and object selected
			if (!selectedPageObjects.length || !selectedPage) {
				throw "ERROR: Page and one object should be selected to perform this action.";
			}

			const pages = state.pages;
			const page: IPage = pages.present[selectedPage as number - 1];
			const pageObjects: PageObjectType[] = page.pageObjects;
			let selectedPageObjectReference = pageObjects[selectedPageObjects[0]];
			if (selectedPageObjects.length > 1) {
				const spo = _.find(pageObjects, (obj) => {
					return obj.shapeId === shapeId;
				});
				if (spo) {
					selectedPageObjectReference = spo;
				}
			}
			const groupSelectionIndex = groupSelectionIndices ? groupSelectionIndices[0] : -1;
			const groupObject = selectedPageObjectReference as IGroupShape;
			if (groupSelectionIndices && groupSelectionIndices.length > 1) {
				const spo = _.find(groupObject.childObjects, (obj) => {
					return obj.shapeId === shapeId;
				});
				if (spo) {
					selectedPageObjectReference = spo;
				}
			} else if (groupSelectionIndex !== undefined && groupSelectionIndex !== -1) {
				selectedPageObjectReference = groupObject.childObjects[groupSelectionIndex!];
			}

			// In case selected object is locked, we don't want to edit the object.
			if (selectedPageObjectReference.spPr.locked) {
				return undefined;
			}

			// Only allow editing of image placeholder description text when editing canvas templates.
			// Editing not allowed in templates if placeholder is part of an attribute placeholder group.
			const placeholderObject = selectedPageObjectReference as IShape;
			// Do allow editing of image placeholder points when editing canvas templates, just not text.
			if (placeholderObject.imagePlaceholder && (!state.canvasEditor.isCanvasTemplate || !custom)) {
				return undefined;
			}

			// Do not allow edit of attribute placeholder text.
			if (placeholderObject.isTextShape && placeholderObject.attributeKey) {
				return undefined;
			}

			// Do not allow edit of rich text placeholders
			if (placeholderObject.txBody?.content.includes(PLACEHOLDER_RICH_TEXT_DIV)) {
				return;
			}

			let mode: CanvasMode = CanvasMode.SELECT;
			const selectedShape = selectedPageObjectReference as IShape;

			selectedShape.hasBeenEdited = true;
			if (custom && selectedShape.spPr.geometry.type !== GeometryTypes.CUSTOM
				&& selectedShape.spPr.geometry.type !== GeometryTypes.PEN) {
				convertShapeToCustom(selectedShape);
			}
			// depending on type of object set appropriate mode
			switch (selectedPageObjectReference.type) {
				case IPageObjectTypes.SHAPE:
					if (exitEdit) {
						break;
					}
					if (selectedShape.isPicture) {
						mode = CanvasMode.EDIT_PICTURE;
					} else if (selectedShape.penShape) {
						mode = CanvasMode.EDIT_PEN;
					} else if (selectedShape.spPr.geometry.type === GeometryTypes.CUSTOM) {
						if (currentCanvasMode !== CanvasMode.EDIT_CURVE) {
							actions.push({
								pointIndex: getCurveSelectionPoint(selectedShape),
								transient: true,
								type: CanvasEditorConstants.SET_CURVE_POINT_INDEX,
							} as ISetSelectedCurvePointAction);
							// only set if current mode is not EDIT_CURVE.
							if (custom
								|| (!selectedShape.allowsFill)) {
								mode = CanvasMode.EDIT_CURVE;
							} else {
								mode = CanvasMode.EDIT_TEXT;
							}
						}
					} else {
						mode = CanvasMode.EDIT_TEXT;
					};
					break;
				case IPageObjectTypes.CONNECTION_SHAPE:
					if (currentCanvasMode !== CanvasMode.EDIT_CURVE) {
						actions.push({
							pointIndex: getCurveSelectionPoint(selectedShape),
							transient: true,
							type: CanvasEditorConstants.SET_CURVE_POINT_INDEX,
						} as ISetSelectedCurvePointAction);
						// only set if current mode is not EDIT_CURVE.
						mode = CanvasMode.EDIT_CURVE;
					}
					break;
				default:
					break;
			}
			// Set canvas mode action
			actions.push({
				transient: true,
				type: CanvasEditorConstants.SET_CANVAS_MODE,
				mode,
			} as ISetCanvasModeAction);

			// Reset guides always if mode changes
			actions.push({
				transient: true,
				type: GuideActionConstants.RESET_GUIDES,
			} as IResetGuidesAction);
			return dispatch(batchActions(actions));
		};

/**
 * Set edit on and off.
 * @param isEditable { boolean } edit mode options .
 * @returns BatchAction/ISetEditAction
 */
export const setEditable: ActionCreator<BatchAction | ISetEditableAction> =
	(isEditable: boolean): BatchAction | ISetEditableAction => {
		// Set editable action
		const editAction: ISetEditableAction = {
			editing: isEditable,
			transient: true,
			type: CanvasEditorConstants.ENABLE_EDITING,
		};
		// Deselect all objects action
		const deselectAllObjects: IDeselectAllObjectAction = {
			transient: true,
			type: SelectionConstants.DESELECT_ALL_OBJECTS,
		};
		// Set canvas mode action
		const setCanvasModeAction = {
			mode: CanvasMode.SELECT,
			transient: true,
			type: CanvasEditorConstants.SET_CANVAS_MODE,
		} as ISetCanvasModeAction;

		if (isEditable) {
			return editAction;
		}
		return batchActions([deselectAllObjects, setCanvasModeAction, editAction]);
	};

/**
 * Set froala license action
 * @param serialKey { string } serial key.
 * @returns set froala license.
 */
export const setFroalaLicense: ActionCreator<ISetFroalaLicenseAction> =
	(serialKey: string): ISetFroalaLicenseAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_FROALA_LICENSE,
		serialKey,
	});

/**
 * Set canvas zoom value action.
 * @param zoomFactor { number } dimension.
 * @returns Set canvas dimension  action.
 */
export const setCanvasZoom: ActionCreator<ISetCanvasZoomAction> =
	(zoomFactor: number): ISetCanvasZoomAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_CANVAS_ZOOM,
		zoomFactor,
	});

/**
 * Set isCanvasTemplate to indicate if user is on template and sets the context type
 * @param isCanvasTemplate { boolean } true if user is editing canvas template.
 * @param contextType {string} context type to use
 */
export const setIsCanvasTemplate: ActionCreator<ISetIsCanvasTemplateAction> =
	(isCanvasTemplate: boolean, contextType?: string): ISetIsCanvasTemplateAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_IS_CANVAS_TEMPLATE,
		contextType,
		isCanvasTemplate,
	});

/**
 * Set canvas container
 * @param canvasContainer { HTMLElement }
 */
export const setCanvasContainer: ActionCreator<ISetCanvasContainerAction> =
	(canvasContainerEl: HTMLElement): ISetCanvasContainerAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_CANVAS_CONTAINER,
		canvasContainerEl,
	});

/**
 * Set selected curve point while editing curve.
 * @param pointIndex { number } selected curve point index.
 * @returns set selected curve point action.
 */
export const setSelectedCurvePoint: ActionCreator<ISetSelectedCurvePointAction> =
	(pointIndex: number, preserveSelection?: boolean): ISetSelectedCurvePointAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_CURVE_POINT_INDEX,
		pointIndex,
		preserveSelection,
	});

/**
 * Set placeholder text format value to "all", "label", or "value".
 * @param {string} placeholderTextFormat
 */
export const setPlaceholderTextFormat: ActionCreator<ISetPlaceholderTextFormatAction> =
	(placeholderTextFormat: string): ISetPlaceholderTextFormatAction => ({
		transient: true,
		type: CanvasEditorConstants.SET_PLACEHOLDER_LABEL_VALUE,
		placeholderTextFormat,
	});

/**
 * Canvas Api
 * Collection of redux dispatch bound actions
 */
export const initCanvasAPI = (dispatch: Dispatch<AnyAction>) => {
	return bindActionCreators({
		editPageObject,
		setCanvasContainer,
		setCanvasDimension,
		setCanvasMode,
		setFormatPainter,
		setFroalaLicense,
		setSnapToGrid,
		setSnapToGuide,
		setEditable,
		setCanvasZoom,
		setPanningMode,
		setIsCanvasTemplate,
		setSelectedCurvePoint,
		setPlaceholderTextFormat,
	}, dispatch);
};
